import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Badge, Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import {statusColors} from '@/types/Transaction';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import TestTransactionSummary from './TestTransactionSummary';
import type {AfriexTestTransaction} from './types';

const TestTransactionTable = ({items}: {items: AfriexTestTransaction[]}) => (
  <Table className="overflow-hidden">
    <Thead>
      <Tr enableHover={false}>
        <Th className="w-20">&nbsp;</Th>
        <Th>Amount</Th>
        <Th>Asset</Th>
        <Th className="w-auto">Status</Th>
        <Th>Type</Th>
        <Th>Processor</Th>
        <Th>Country</Th>
        <Th>Date Initiated</Th>
      </Tr>
    </Thead>
    <TBody>
      {items.length === 0 ? (
        <Tr enableHover={false}>
          <Td colSpan={8} className="text-center py-6 text-gray-500">
            No test transactions found
          </Td>
        </Tr>
      ) : (
        items.map((item, index) => (
          <TRow key={item.id} item={item} index={index} />
        ))
      )}
    </TBody>
  </Table>
);

const TRow = ({item, index}: {item: AfriexTestTransaction; index: number}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700 dark:text-gray-100" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="font-medium">{item.amount}</Td>
        <Td>{item.asset}</Td>
        <Td>
          <Badge label={item.status} colorScheme={statusColors[item.status]} />
        </Td>
        <Td className="capitalize">
          {capitalizeWords(item.type?.replace(/_/g, ' '))}
        </Td>
        <Td>{item.processor}</Td>
        <Td>{item.country}</Td>
        <Td>{formatDate.DDMonYYYY(item.createdAt ?? '')}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={8} className="p-0 m-0 whitespace-nowrap">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <TestTransactionSummary data={item} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default TestTransactionTable;
